import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./use-auth";
import type { UserRole } from "@/features/accounts/types/account.types";

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export function RequireRole({
  roles,
  children,
}: {
  roles: UserRole[];
  children: React.ReactNode;
}) {
  const { role } = useAuth();

  if (!role || !roles.includes(role)) {
    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
}

export function RequirePasswordReset({
  children,
}: {
  children: React.ReactNode;
}) {
  const { requirePasswordReset } = useAuth();
  const location = useLocation();

  const allowedPaths = ["/settings/profile/change-password", "/invitation"];
  const isAllowed = allowedPaths.some((p) => location.pathname.startsWith(p));

  if (requirePasswordReset && !isAllowed) {
    return <Navigate to="/settings/profile/change-password" replace />;
  }

  return <>{children}</>;
}
